import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getHotArticles, getArticlesByCategory } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';

const articleImages: Record<string, string> = {
  'jd-618-super-discount-2026': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20lifestyle%20scene%20with%20warm%20golden%20tones%2C%20clean%20composition%2C%20soft%20natural%20lighting%2C%20elegant%20and%20simple%20aesthetic%2C%20high%20quality%20editorial%20photography&width=1200&height=500&seq=art-jd618-detail&orientation=landscape',
  'video-membership-comparison-2026': 'https://readdy.ai/api/search-image?query=Cozy%20living%20room%20with%20large%20TV%20screen%2C%20warm%20lighting%2C%20modern%20minimalist%20interior%2C%20comfortable%20sofa%2C%20entertainment%20setup%2C%20soft%20golden%20hour%20light%2C%20clean%20aesthetic&width=1200&height=500&seq=art-video-detail&orientation=landscape',
  'shenzhen-consumer-voucher-2026': 'https://readdy.ai/api/search-image?query=Bright%20modern%20cityscape%20of%20Shenzhen%20with%20warm%20sunset%20light%2C%20clean%20architecture%2C%20shopping%20district%2C%20vibrant%20urban%20scene%2C%20golden%20hour%20glow%2C%20elegant%20composition&width=1200&height=500&seq=art-sz-voucher-detail&orientation=landscape',
  'year-end-bonus-tax-planning': 'https://readdy.ai/api/search-image?query=Clean%20modern%20office%20desk%20with%20calculator%20and%20documents%2C%20warm%20natural%20lighting%2C%20professional%20minimalist%20workspace%2C%20soft%20golden%20tones%2C%20elegant%20composition&width=1200&height=500&seq=art-tax-detail&orientation=landscape',
  'airline-mileage-redemption-guide': 'https://readdy.ai/api/search-image?query=Airplane%20wing%20view%20above%20clouds%20at%20sunset%2C%20warm%20golden%20sky%2C%20travel%20inspiration%2C%20clean%20minimalist%20aerial%20view%2C%20peaceful%20atmosphere&width=1200&height=500&seq=art-mileage-detail&orientation=landscape',
  'online-course-certificate-subsidy': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20study%20desk%20with%20laptop%20and%20books%2C%20warm%20natural%20light%2C%20clean%20organized%20workspace%2C%20soft%20beige%20tones%2C%20elegant%20educational%20aesthetic&width=1200&height=500&seq=art-course-detail&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <main className="pt-20">
        <div className="w-full px-4 md:px-6 py-20 text-center">
          <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-6">
            <i className="ri-file-unknow-line text-3xl"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-3">
            文章未找到
          </h1>
          <p className="text-sm text-foreground-500 mb-6">
            您访问的文章不存在或已被移除
          </p>
          <Link
            to="/"
            className="inline-flex px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedArticles = getArticlesByCategory(article.category)
    .filter((a) => a.slug !== article.slug)
    .slice(0, 3);
  const hotArticles = getHotArticles()
    .filter((a) => a.slug !== article.slug)
    .slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    url: `https://fulidaohang.com/article/${article.slug}`,
    author: {
      '@type': 'Organization',
      name: '福利导航',
    },
    publisher: {
      '@type': 'Organization',
      name: '福利导航',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <main className="pt-20">
        <div className="w-full px-4 md:px-6 py-6 bg-background-50 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="mb-6">
                <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                  {article.categoryName}
                </span>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-400">
                  <span>{article.date}</span>
                  <span>·</span>
                  <span>阅读时间约{article.readTime}</span>
                  <span>·</span>
                  <span>福利导航编辑团队</span>
                </div>
              </div>

              <div className="relative h-64 md:h-80 overflow-hidden rounded-lg mb-10">
                <img
                  src={articleImages[article.slug] || articleImages['jd-618-super-discount-2026']}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div
                className="prose-custom text-sm text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </article>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h4 className="text-lg font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[...relatedArticles, ...hotArticles].slice(0, 3).map((a) => (
                  <Link
                    key={a.slug}
                    to={`/article/${a.slug}`}
                    className="group p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer"
                  >
                    <span className="inline-block px-2 py-0.5 rounded bg-secondary-100 text-secondary-700 text-xs mb-2">
                      {a.categoryName}
                    </span>
                    <h5 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                      {a.title}
                    </h5>
                    <p className="text-xs text-foreground-400 mt-2">{a.date}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}